const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api';

function getAuthToken() {
  return localStorage.getItem('e3_auth_token');
}

async function request(url, options = {}) {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${url}`, { ...options, headers });
  if (res.status === 401) {
    // Token expired or invalid — clear it
    localStorage.removeItem('e3_auth_token');
    localStorage.removeItem('e3_user');
    window.dispatchEvent(new Event('auth-expired'));
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(body.error || 'Request failed');
    err.status = res.status;
    err.code = body.code;
    // Preserve the full response body so callers can read structured
    // fields beyond `error`/`code` -- e.g. the CE-sync ordering 409
    // returns blocking_period_id / blocking_period_name so the UI can
    // surface a "Retry the earlier period first" dialog naming the
    // specific row that's blocking.
    err.body = body;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

// Auth
export const getMe = () => request('/auth/me');
export const getUsers = () => request('/auth/users');
export const createUser = (data) => request('/auth/users', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (id, data) => request(`/auth/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteUser = (id) => request(`/auth/users/${id}`, { method: 'DELETE' });
export const getPMList = () => request('/auth/pm-list');
export const getRoles = () => request('/auth/roles');
export const getCapabilities = () => request('/auth/capabilities');
export const getOrgTree = () => request('/auth/org-tree');
export const getSubordinates = (userId) => request(`/auth/subordinates/${userId}`);

// Projects
export const getProjects = (opts = {}) => {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.exclude_status) params.set('exclude_status', opts.exclude_status);
  const qs = params.toString();
  return request(`/projects${qs ? '?' + qs : ''}`);
};
export const getProject = (id) => request(`/projects/${id}`);
export const getProjectBySlug = (slug) => request(`/projects/by-slug/${encodeURIComponent(slug)}`);
export const getProjectSummary = (id) => request(`/projects/${id}/summary`);
export const createProject = (data) => request('/projects', { method: 'POST', body: JSON.stringify(data) });
export const createIntakeProject = (data) => request('/projects/intake', { method: 'POST', body: JSON.stringify(data) });
export const assignPM = (id, data) => request(`/projects/${id}/assign-pm`, { method: 'PUT', body: JSON.stringify(data) });
export const updateProject = (id, data) => request(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteProject = (id) => request(`/projects/${id}`, { method: 'DELETE' });
export const uploadProjectLogo = (id, logo_url) => request(`/projects/${id}/logo`, { method: 'PUT', body: JSON.stringify({ logo_url }) });

// SOV Lines
export const getSOVLines = (projectId) => request(`/sov-lines/project/${projectId}`);
export const getSOVLine = (id) => request(`/sov-lines/${id}`);
export const createSOVLine = (data) => request('/sov-lines', { method: 'POST', body: JSON.stringify(data) });
export const bulkCreateSOVLines = (project_id, lines) =>
  request('/sov-lines/bulk', { method: 'POST', body: JSON.stringify({ project_id, lines }) });
export const updateSOVLine = (id, data) => request(`/sov-lines/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteSOVLine = (id) => request(`/sov-lines/${id}`, { method: 'DELETE' });
export const checkSOVAllocation = (projectId) => request(`/sov-lines/project/${projectId}/allocation-check`);
export const getSOVAllocatedTotals = (projectId) => request(`/sov-lines/project/${projectId}/allocated-totals`);
export const rollupSOVLine = (id) => request(`/sov-lines/${id}/rollup`, { method: 'POST' });
export const getRollupSummary = (projectId) => request(`/sov-lines/project/${projectId}/rollup-summary`);
export const resetSOVProgress = (projectId) => request(`/sov-lines/project/${projectId}/reset-progress`, { method: 'POST' });
export const getSOVChangeLog = (id) => request(`/sov-lines/${id}/change-log`);
export const getSOVProjectChangeLog = (projectId) => request(`/sov-lines/project/${projectId}/change-log`);

// Tasks
export const getTasks = (projectId) => request(`/tasks/project/${projectId}`);
export const getTask = (id) => request(`/tasks/${id}`);
export const createTask = (data) => request('/tasks', { method: 'POST', body: JSON.stringify(data) });
export const bulkCreateTasks = (project_id, tasks) =>
  request('/tasks/bulk', { method: 'POST', body: JSON.stringify({ project_id, tasks }) });
export const updateTask = (id, data) => request(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTask = (id) => request(`/tasks/${id}`, { method: 'DELETE' });
export const getGanttData = (projectId) => request(`/tasks/project/${projectId}/gantt`);
export const addPredecessor = (taskId, data) => request(`/tasks/${taskId}/predecessors`, { method: 'POST', body: JSON.stringify(data) });
export const updatePredecessor = (taskId, predId, data) => request(`/tasks/${taskId}/predecessors/${predId}`, { method: 'PUT', body: JSON.stringify(data) });
export const removePredecessor = (taskId, predId) => request(`/tasks/${taskId}/predecessors/${predId}`, { method: 'DELETE' });
export const getDependencyAlerts = (projectId) => request(`/tasks/project/${projectId}/dependency-alerts`);
export const getAllDependencyAlerts = () => request('/tasks/all-dependency-alerts');

// Sections
export const getSections = (projectId) => request(`/sections/project/${projectId}`);
export const createSection = (data) => request('/sections', { method: 'POST', body: JSON.stringify(data) });

// Schedule-tab sections (separate from SOV sections by design — different
// real-world concept: SOV groups costs, Schedule groups work). Same shape
// as the sections API so the TasksTab swap is mechanical.
export const getScheduleSections = (projectId) => request(`/schedule-sections/project/${projectId}`);
export const createScheduleSection = (data) => request('/schedule-sections', { method: 'POST', body: JSON.stringify(data) });
export const deleteScheduleSection = (id) => request(`/schedule-sections/${id}`, { method: 'DELETE' });
export const updateScheduleSection = (id, data) => request(`/schedule-sections/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const reorderScheduleSections = (projectId, order) => request(`/schedule-sections/project/${projectId}/reorder`, { method: 'PUT', body: JSON.stringify({ order }) });
export const updateSection = (id, data) => request(`/sections/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteSection = (id) => request(`/sections/${id}`, { method: 'DELETE' });
export const reorderSections = (projectId, order) => request(`/sections/project/${projectId}/reorder`, { method: 'PUT', body: JSON.stringify({ order }) });

// Allocations
export const getAllocations = (projectId) => request(`/allocations/project/${projectId}`);
export const getSOVAllocations = (sovLineId) => request(`/allocations/sov/${sovLineId}`);
export const getTaskAllocations = (taskId) => request(`/allocations/task/${taskId}`);
export const createAllocation = (data) => request('/allocations', { method: 'POST', body: JSON.stringify(data) });
export const updateAllocation = (id, data) => request(`/allocations/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAllocation = (id) => request(`/allocations/${id}`, { method: 'DELETE' });

// Billing
export const getBillingPeriods = (projectId) => request(`/billing/project/${projectId}`);
export const getBillingPeriod = (id) => request(`/billing/${id}`);
export const createBillingPeriod = (data) => request('/billing', { method: 'POST', body: JSON.stringify(data) });
export const updateBillingPeriod = (id, data) => request(`/billing/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteBillingPeriod = (id) => request(`/billing/${id}`, { method: 'DELETE' });
export const generateBillingLines = (id) => request(`/billing/${id}/generate-lines`, { method: 'POST' });
export const updateBillingLine = (lineId, data) => request(`/billing/lines/${lineId}`, { method: 'PUT', body: JSON.stringify(data) });
export const approveBilling = (id) => request(`/billing/${id}/approve`, { method: 'POST' });
export const lockBilling = (id) => request(`/billing/${id}/lock`, { method: 'POST' });
export const advanceBillingStatus = (id) => request(`/billing/${id}/advance-status`, { method: 'POST' });
// Retry a CE-Agent sync that failed on the original Finalize (e.g.
// network blip, agent restart). Server runs the same submission
// path without re-running rollup; period stays Finalized either way.
// 409 with err.body.blocking_period_name when an earlier period
// must be synced first; 502 with err.body.period containing the
// fresh DB row when the submission errored.
export const retryBillingCESync = (id) => request(`/billing/${id}/retry-ce-sync`, { method: 'POST' });
export const createRevisedBillingPeriod = (id) => request(`/billing/${id}/create-revised`, { method: 'POST' });
export const getScheduleComparison = (id) => request(`/billing/${id}/schedule-comparison`);
export const getSchedulePerformance = (projectId) => request(`/billing/project/${projectId}/schedule-performance`);
export const getBillingPerformance = (projectId) => request(`/billing/project/${projectId}/billing-performance`);
export const refreshFromSchedule = (id) => request(`/billing/${id}/refresh-from-schedule`, { method: 'POST' });
export const getProgressAlerts = (id) => request(`/billing/${id}/progress-alerts`);
export const getProgressAlertsSummary = (projectId) => request(`/billing/project/${projectId}/progress-alerts-summary`);
export const getAllProjectsAlerts = () => request('/billing/all-projects-alerts');

// Accounts Receivable
export const getAccountsReceivable = () => request('/ar');

// AR mirror (CE actuals synced by CE-Agent — see /api/ar-sync)
export const getArSyncAging = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/ar-sync/aging${qs ? '?' + qs : ''}`);
};
export const getArSyncAgingSnapshots = () => request('/ar-sync/aging/snapshots');
export const getArSyncCollections = (period) =>
  request(`/ar-sync/collections${period ? `?period=${encodeURIComponent(period)}` : ''}`);
export const getArSyncCollectionPeriods = () => request('/ar-sync/collections/periods');
// AR sync settings (scheduler cadence, pull-now, backfill) — Settings → AR Sync panel
export const getArSyncSettings = () => request('/ar-sync/settings');
export const setArSyncCadence = (cadence) => request('/ar-sync/cadence', { method: 'POST', body: JSON.stringify({ cadence }) });
export const pullArSyncNow = (repull = false) => request('/ar-sync/pull-current', { method: 'POST', body: JSON.stringify({ repull }) });
export const getArSyncBackfill = () => request('/ar-sync/backfill');
export const runArSyncBackfill = (startMonth) => request('/ar-sync/backfill', { method: 'POST', body: JSON.stringify(startMonth ? { start_month: startMonth } : {}) });
export const getArNotes = () => request('/ar-notes');
export const addArNote = (payload) => request('/ar-notes', { method: 'POST', body: JSON.stringify(payload) });

// Schedule Change Log
export const getScheduleChangeLog = (projectId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/schedule-log/project/${projectId}${qs ? '?' + qs : ''}`);
};
export const getScheduleChangeSummary = (projectId) => request(`/schedule-log/project/${projectId}/summary`);
export const getTaskHistory = (projectId, taskId) => request(`/schedule-log/project/${projectId}/task/${taskId}/history`);
export const getScheduleLogPMs = () => request('/schedule-log/pms');
export const getPMScheduleSummary = (pmName) => request(`/schedule-log/pm/${encodeURIComponent(pmName)}/summary`);

// Change Orders
export const getChangeOrders = (projectId) => request(`/change-orders/project/${projectId}`);
export const getChangeOrder = (id) => request(`/change-orders/${id}`);
export const createChangeOrder = (data) => request('/change-orders', { method: 'POST', body: JSON.stringify(data) });
export const updateChangeOrder = (id, data) => request(`/change-orders/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteChangeOrder = (id) => request(`/change-orders/${id}`, { method: 'DELETE' });

// Contract Value Log
export const getContractValueLog = (projectId) => request(`/contract-value-log/project/${projectId}`);
export const getContractValueSummary = (projectId) => request(`/contract-value-log/project/${projectId}/summary`);
export const createManualAdjustment = (data) => request('/contract-value-log/manual-adjustment', { method: 'POST', body: JSON.stringify(data) });

// Forecast
export const getForecasts = (projectId) => request(`/forecast/project/${projectId}`);
export const createForecast = (data) => request('/forecast', { method: 'POST', body: JSON.stringify(data) });
export const updateForecast = (id, data) => request(`/forecast/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteForecast = (id) => request(`/forecast/${id}`, { method: 'DELETE' });
export const autoGenerateForecast = (projectId) => request(`/forecast/project/${projectId}/auto-generate`, { method: 'POST' });
export const getForecastDashboard = (projectId) => request(`/forecast/project/${projectId}/dashboard`);
// Portfolio-wide projected monthly billings across all active projects.
export const getBillingsProjection = () => request('/forecast/all-projects/dashboard');

// Retainage Release
export const releaseRetainage = (sovLineId, data) => request(`/sov-lines/${sovLineId}/release-retainage`, { method: 'POST', body: JSON.stringify(data) });
export const unreleaseRetainage = (sovLineId, data) => request(`/sov-lines/${sovLineId}/unrelease-retainage`, { method: 'POST', body: JSON.stringify(data) });

// Workload
export const getWorkloadDashboard = () => request('/workload/dashboard');
export const updatePMCapacity = (pm_name, capacity_cap) => request('/workload/pm-cap', { method: 'PUT', body: JSON.stringify({ pm_name, capacity_cap }) });

// Exports
export const getExportLogs = (projectId) => request(`/exports/project/${projectId}`);
export const generateBillingExport = (data) => request('/exports/billing-export', { method: 'POST', body: JSON.stringify(data) });
export const generateClientSchedule = (data) => request('/exports/client-schedule', { method: 'POST', body: JSON.stringify(data) });

// Audit Log
export const getAuditLog = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/audit-log${qs ? '?' + qs : ''}`);
};
export const getAuditSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/audit-log/summary${qs ? '?' + qs : ''}`);
};

// CE Activity (role-scoped CE Agent job history -- read-only)
export const getCEActivity = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/ce-activity${qs ? '?' + qs : ''}`);
};

// Sync — CE Financial Data
export const triggerSyncJobCost = (data) => request('/sync/job-cost', { method: 'POST', body: JSON.stringify(data) });
export const triggerSyncAPAR = (data) => request('/sync/ap-ar', { method: 'POST', body: JSON.stringify(data) });
export const getSyncJobStatus = (ceJobId) => request(`/sync/job/${ceJobId}`);
export const getSyncHistory = (limit = 20) => request(`/sync/history?limit=${limit}`);
export const getLastSync = () => request('/sync/last-sync');
export const getSyncJobCosts = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs${qs ? `?${qs}` : ''}`);
};
export const getSyncJobCostSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/summary${qs ? `?${qs}` : ''}`);
};

// Cumulative (to-date) job cost data
export const getSyncJobCostsCurrent = (jobId) => request(`/sync/job-costs/current?job_id=${encodeURIComponent(jobId)}`);
export const getSyncJobCostCurrentSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/current/summary${qs ? `?${qs}` : ''}`);
};
// Archived jobs' summaries, grouped by archive_year — for the Job Costs "Archived" section.
export const getSyncJobCostArchivedSummary = () => request('/sync/job-costs/archived/summary');

// Monthly breakdown
export const getSyncJobCostsMonthly = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/monthly${qs ? `?${qs}` : ''}`);
};
export const getSyncJobCostMonthlySummary = (jobId) => request(`/sync/job-costs/monthly/summary?job_id=${encodeURIComponent(jobId)}`);

// Phase drill-down & period list
export const getSyncJobCostPhases = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/phases${qs ? `?${qs}` : ''}`);
};
// Department Status (Finance) — budget variance by CE phase + booked/current GM.
export const getDeptStatus = () => request('/dept-status');

export const getSyncJobCostPeriods = (jobId) => request(`/sync/job-costs/periods?job_id=${encodeURIComponent(jobId)}`);

export const getSyncAPAR = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/ap-ar${qs ? `?${qs}` : ''}`);
};
export const getSyncARaging = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/ap-ar/aging${qs ? `?${qs}` : ''}`);
};
export const getSyncDashboard = (jobId) => request(`/sync/dashboard/${jobId}`);

// Cost Code Notes
export const getCostCodeNotes = (jobId) => request(`/cost-code-notes?job_id=${encodeURIComponent(jobId)}`);
export const addCostCodeNote = (data) => request('/cost-code-notes', { method: 'POST', body: JSON.stringify(data) });
export const deleteCostCodeNote = (noteId) => request(`/cost-code-notes/${noteId}`, { method: 'DELETE' });

// Notifications
export const getNotifications = () => request('/notifications');
export const getNotificationCount = () => request('/notifications/count');
export const dismissNotification = (data) => request('/notifications/dismiss', { method: 'POST', body: JSON.stringify(data) });

// Job Cost Roster (admin — Settings) — roster of CE job numbers the team
// pulls job cost for. Summit drives the CE-Agent's inception-to-date pull.
export const getJobCostRoster = () => request('/job-cost-roster');
export const saveJobCostRosterEntry = (data) => request('/job-cost-roster', { method: 'POST', body: JSON.stringify(data) });
export const deleteJobCostRosterEntry = (id) => request(`/job-cost-roster/${id}`, { method: 'DELETE' });
// Lifecycle: archive to a year bucket (keeps data), restore to active, or hard-delete (purges cost data).
export const archiveJobCostRosterEntry = (id, year) => request(`/job-cost-roster/${id}/archive`, { method: 'POST', body: JSON.stringify({ year }) });
export const restoreJobCostRosterEntry = (id) => request(`/job-cost-roster/${id}/restore`, { method: 'POST' });
export const purgeJobCostRosterEntry = (id) => request(`/job-cost-roster/${id}/delete`, { method: 'POST' });
export const pullJobCostRoster = () => request('/job-cost-roster/pull-all', { method: 'POST' });
export const pullJobCostRosterOne = (id) => request(`/job-cost-roster/${id}/pull`, { method: 'POST' });
export const setJobCostRosterCadence = (cadence) => request('/job-cost-roster/cadence', { method: 'POST', body: JSON.stringify({ cadence }) });
// Historical backfill — per-job month-by-month import of past job cost.
export const getJobCostBackfill = () => request('/job-cost-roster/backfill');
export const runJobCostBackfill = (job_number, start_month) => request('/job-cost-roster/backfill', { method: 'POST', body: JSON.stringify({ job_number, start_month }) });
export const setJobCostBackfillStart = (job_number, start_month) => request('/job-cost-roster/backfill/start', { method: 'POST', body: JSON.stringify({ job_number, start_month }) });

// Notification Lists (admin)
export const getNotificationLists = () => request('/notifications/lists');
export const createNotificationList = (data) => request('/notifications/lists', { method: 'POST', body: JSON.stringify(data) });
export const updateNotificationList = (id, data) => request(`/notifications/lists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const getListMembers = (listId) => request(`/notifications/lists/${listId}/members`);
export const addListMember = (listId, data) => request(`/notifications/lists/${listId}/members`, { method: 'POST', body: JSON.stringify(data) });
export const removeListMember = (listId, memberId) => request(`/notifications/lists/${listId}/members/${memberId}`, { method: 'DELETE' });
